import { Link } from 'react-router-dom';
import { VideoItem } from '@/mocks/videos';
import { VideoCategory } from '@/mocks/categories';

interface HeroSectionProps {
  featuredVideo: VideoItem;
  categories: VideoCategory[];
}

export default function HeroSection({ featuredVideo, categories }: HeroSectionProps) {
  return (
    <section className="relative w-full min-h-[520px] md:min-h-[600px] flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={featuredVideo.thumbnail}
          alt={featuredVideo.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-background-50"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-transparent"></div>
      </div>

      <div className="container-site relative z-10 w-full py-16 md:py-20">
        <div className="max-w-2xl">
          <div className="flex items-center gap-3 mb-4">
            <span className="px-3 py-1 text-xs font-medium bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full whitespace-nowrap">
              正在热播
            </span>
            <span className="text-sm text-foreground-300 flex items-center gap-1">
              <i className="ri-star-fill text-accent-500 text-xs"></i>
              {featuredVideo.rating}
            </span>
            <span className="text-sm text-foreground-300">{featuredVideo.quality}</span>
            <span className="text-sm text-foreground-300">{featuredVideo.duration}</span>
          </div>

          <h1 className="text-3xl md:text-5xl font-bold text-foreground-950 mb-4 leading-tight">
            {featuredVideo.title}
          </h1>
          <p className="text-sm md:text-base text-foreground-400 mb-6 leading-relaxed max-w-xl line-clamp-2">
            {featuredVideo.description}
          </p>

          <div className="flex items-center gap-3 flex-wrap">
            <Link
              to={`/article/${featuredVideo.id}`}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-fill text-lg"></i>
              立即观看
            </Link>
            <Link
              to={`/category/${featuredVideo.category}`}
              className="inline-flex items-center gap-2 px-6 py-3 bg-background-50/10 hover:bg-background-50/20 text-foreground-200 border border-foreground-200/20 rounded-full text-sm transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-list-check"></i>
              更多推荐
            </Link>
          </div>

          <div className="flex items-center gap-2 mt-6 text-xs text-foreground-500 flex-wrap">
            <span>来源：</span>
            <span className="font-medium text-foreground-400">{featuredVideo.source}</span>
            <span className="text-foreground-600">|</span>
            <span>{featuredVideo.views.toLocaleString()} 次播放</span>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0">
        <div className="container-site pb-6">
          <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map(cat => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex-shrink-0 flex items-center gap-2 px-4 py-2.5 bg-background-100/80 backdrop-blur-sm border border-background-300/30 rounded-full text-sm text-foreground-600 hover:text-primary-500 hover:border-primary-500/30 transition-all whitespace-nowrap cursor-pointer"
              >
                <i className={`${cat.icon} text-primary-500 text-base`}></i>
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}